import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import {
    buildChannelSplitSvgDocument,
    channelCount,
    extractSvgInnerContent,
    parseViewBoxParts,
    recolorSvgMarkupForChannel,
    type ChannelCompositeMode,
    type ChannelDistortionMethod,
    viewBoxAttrFromSvg,
} from "../../utils/channelSplitVector";
import {
    displaceSvgContent,
    type NoiseDisplacementStyle,
} from "../../utils/displacementMap";
import { useSvgToolHandoffReceiver } from "../../hooks/useSvgToolHandoffReceiver";
import { isValidSvg } from "../../utils/svgUtils";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";

interface UploadedSvg {
    name: string;
    content: string;
}

interface ChannelSplitState {
    svg: UploadedSvg | null;
    error: string | null;
    svgInputRef: React.RefObject<HTMLInputElement>;
    handleSvgLoad: (e: React.ChangeEvent<HTMLInputElement>) => void;
    compositeMode: ChannelCompositeMode;
    setCompositeMode: (m: ChannelCompositeMode) => void;
    distortionMethod: ChannelDistortionMethod;
    setDistortionMethod: (m: ChannelDistortionMethod) => void;
    pathNoiseStyle: NoiseDisplacementStyle;
    setPathNoiseStyle: (s: NoiseDisplacementStyle) => void;
    roughen: number[];
    setRoughen: (v: number[]) => void;
    setRoughenAt: (index: number, value: number) => void;
    xOffsets: number[];
    yOffsets: number[];
    setXOffsetAt: (index: number, value: number) => void;
    setYOffsetAt: (index: number, value: number) => void;
    rotationsDeg: number[];
    setRotationDegAt: (index: number, value: number) => void;
    resetOffsets: () => void;
    channelVisible: boolean[];
    setChannelVisibleAt: (index: number, visible: boolean) => void;
    resetChannelVisibility: () => void;
    viewBoxAttr: string;
    innerMarkup: string;
    vb: { x: number; y: number; width: number; height: number };
    exportSvgString: string | null;
    canExport: boolean;
    handleDownload: () => void;
    applyPreset: (values: Record<string, unknown>) => void;
    getPresetValues: () => Record<string, unknown>;
    resetParameters: () => void;
}

const ChannelSplitContext = createContext<ChannelSplitState | null>(null);

export function useChannelSplit() {
    const ctx = useContext(ChannelSplitContext);
    if (!ctx) throw new Error("useChannelSplit must be used within ChannelSplitProvider");
    return ctx;
}

function defaultRoughen(mode: ChannelCompositeMode): number[] {
    const n = channelCount(mode);
    return Array.from({ length: n }, () => 0);
}

function defaultChannelOffsets(mode: ChannelCompositeMode): number[] {
    const n = channelCount(mode);
    return Array.from({ length: n }, () => 0);
}

function defaultChannelVisibility(mode: ChannelCompositeMode): boolean[] {
    const n = channelCount(mode);
    return Array.from({ length: n }, () => true);
}

const DEFAULT_CHANNEL_SPLIT_PRESET: Record<string, unknown> = {
    compositeMode: "rgb-screen",
    distortionMethod: "filter-displacement",
    pathNoiseStyle: "fbm",
    roughen: defaultRoughen("rgb-screen"),
    xOffsets: defaultChannelOffsets("rgb-screen"),
    yOffsets: defaultChannelOffsets("rgb-screen"),
    rotationsDeg: defaultChannelOffsets("rgb-screen"),
    channelVisible: defaultChannelVisibility("rgb-screen"),
};

export function ChannelSplitProvider({ children }: { children: React.ReactNode }) {
    const [svg, setSvg] = useState<UploadedSvg | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [compositeMode, setCompositeModeState] = useState<ChannelCompositeMode>("rgb-screen");
    const [distortionMethod, setDistortionMethod] =
        useState<ChannelDistortionMethod>("filter-displacement");
    const [pathNoiseStyle, setPathNoiseStyle] = useState<NoiseDisplacementStyle>("fbm");
    const [roughen, setRoughen] = useState<number[]>(() => defaultRoughen("rgb-screen"));
    const [xOffsets, setXOffsets] = useState<number[]>(() => defaultChannelOffsets("rgb-screen"));
    const [yOffsets, setYOffsets] = useState<number[]>(() => defaultChannelOffsets("rgb-screen"));
    const [rotationsDeg, setRotationsDeg] = useState<number[]>(() =>
        defaultChannelOffsets("rgb-screen"),
    );
    const [channelVisible, setChannelVisible] = useState<boolean[]>(
        () => defaultChannelVisibility("rgb-screen")
    );
    const svgInputRef = useRef<HTMLInputElement | null>(null);

    const setCompositeMode = useCallback((m: ChannelCompositeMode) => {
        setCompositeModeState(m);
        setRoughen((prev) => {
            const n = channelCount(m);
            const next = prev.slice(0, n);
            while (next.length < n) next.push(0);
            return next;
        });
        setXOffsets((prev) => {
            const n = channelCount(m);
            const next = prev.slice(0, n);
            while (next.length < n) next.push(0);
            return next;
        });
        setYOffsets((prev) => {
            const n = channelCount(m);
            const next = prev.slice(0, n);
            while (next.length < n) next.push(0);
            return next;
        });
        setRotationsDeg((prev) => {
            const n = channelCount(m);
            const next = prev.slice(0, n);
            while (next.length < n) next.push(0);
            return next;
        });
        setChannelVisible((prev) => {
            const n = channelCount(m);
            const next = prev.slice(0, n);
            while (next.length < n) next.push(true);
            return next;
        });
    }, []);

    const viewBoxAttr = useMemo(
        () => (svg ? viewBoxAttrFromSvg(svg.content) : "0 0 100 100"),
        [svg]
    );

    const innerMarkup = useMemo(
        () => (svg ? extractSvgInnerContent(svg.content) : ""),
        [svg]
    );

    const vb = useMemo(() => {
        const p = parseViewBoxParts(viewBoxAttr);
        return p ?? { x: 0, y: 0, width: 100, height: 100 };
    }, [viewBoxAttr]);

    const handleSvgLoad = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setError(null);

        if (!file.type.includes("svg") && !file.name.toLowerCase().endsWith(".svg")) {
            setError("Please choose an SVG file.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            const text = typeof reader.result === "string" ? reader.result : "";
            if (!text) {
                setError("Unable to read SVG file.");
                return;
            }
            if (!isValidSvg(text)) {
                setError("File does not appear to be valid SVG.");
                return;
            }
            setSvg({ name: file.name, content: text });
        };
        reader.onerror = () => setError("Error reading file.");
        reader.readAsText(file);
    }, []);

    const setRoughenAt = useCallback((index: number, value: number) => {
        setRoughen((prev) => {
            const next = [...prev];
            next[index] = value;
            return next;
        });
    }, []);
    const setXOffsetAt = useCallback((index: number, value: number) => {
        const safeValue = Number.isFinite(value) ? value : 0;
        setXOffsets((prev) => {
            const next = [...prev];
            next[index] = safeValue;
            return next;
        });
    }, []);
    const setYOffsetAt = useCallback((index: number, value: number) => {
        const safeValue = Number.isFinite(value) ? value : 0;
        setYOffsets((prev) => {
            const next = [...prev];
            next[index] = safeValue;
            return next;
        });
    }, []);
    const setRotationDegAt = useCallback((index: number, value: number) => {
        const safeValue = Number.isFinite(value) ? value : 0;
        setRotationsDeg((prev) => {
            const next = [...prev];
            next[index] = safeValue;
            return next;
        });
    }, []);
    const resetOffsets = useCallback(() => {
        const zeroes = defaultChannelOffsets(compositeMode);
        setXOffsets(zeroes);
        setYOffsets(zeroes);
        setRotationsDeg(zeroes);
    }, [compositeMode]);
    const setChannelVisibleAt = useCallback((index: number, visible: boolean) => {
        setChannelVisible((prev) => {
            const next = [...prev];
            next[index] = visible;
            return next;
        });
    }, []);
    const resetChannelVisibility = useCallback(() => {
        setChannelVisible(defaultChannelVisibility(compositeMode));
    }, [compositeMode]);

    const exportSvgString = useMemo(() => {
        if (!svg || !innerMarkup) return null;
        const symbolId = "channelSplitSrc";
        const filterIdPrefix = "channelSplitFlt";
        const layerMarkups =
            distortionMethod === "path-displacement"
                ? roughen.map((rough, i) => {
                      const sourceMarkup =
                          rough <= 0
                              ? innerMarkup
                              : extractSvgInnerContent(
                                    displaceSvgContent(
                                        `<svg viewBox="${viewBoxAttr}" xmlns="http://www.w3.org/2000/svg">${innerMarkup}</svg>`,
                                        {
                                            strength: rough,
                                            circleSegments: 48,
                                            mode: "noise",
                                            seed: `channel-${i}`,
                                            perShapeSeededNoise: true,
                                            noiseScale: 0.045,
                                            octaves: 2,
                                            noiseStyle: pathNoiseStyle,
                                            imageData: null,
                                            channelMode: "rg",
                                            svgMinX: vb.x,
                                            svgMinY: vb.y,
                                            svgWidth: vb.width,
                                            svgHeight: vb.height,
                                        }
                                    )
                                );
                      return recolorSvgMarkupForChannel(sourceMarkup, compositeMode, i);
                  })
                : undefined;
        return buildChannelSplitSvgDocument({
            viewBoxAttr,
            vb,
            symbolId,
            filterIdPrefix,
            innerMarkup,
            mode: compositeMode,
            roughenScales: roughen,
            xOffsets,
            yOffsets,
            rotationsDeg,
            channelVisible,
            layerMarkups,
            distortionMethod,
        });
    }, [svg, innerMarkup, viewBoxAttr, vb, compositeMode, roughen, xOffsets, yOffsets, rotationsDeg, channelVisible, distortionMethod, pathNoiseStyle]);

    const canExport = !!exportSvgString;

    const handleDownload = useCallback(() => {
        if (!exportSvgString || !svg) return;
        const blob = new Blob([exportSvgString], { type: "image/svg+xml;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        const base = svg.name.replace(/\.svg$/i, "");
        link.download = `${base}-channels.svg`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }, [exportSvgString, svg]);

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            compositeMode, distortionMethod, pathNoiseStyle, roughen,
            xOffsets, yOffsets, rotationsDeg,
            channelVisible,
        }),
        [compositeMode, distortionMethod, pathNoiseStyle, roughen, xOffsets, yOffsets, rotationsDeg, channelVisible],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (values.compositeMode != null) setCompositeMode(values.compositeMode as ChannelCompositeMode);
        if (values.distortionMethod != null) setDistortionMethod(values.distortionMethod as ChannelDistortionMethod);
        if (values.pathNoiseStyle != null) {
            setPathNoiseStyle(values.pathNoiseStyle as NoiseDisplacementStyle);
        }
        if (values.roughen != null) setRoughen(values.roughen as number[]);
        if (values.xOffsets != null) setXOffsets(values.xOffsets as number[]);
        if (values.yOffsets != null) setYOffsets(values.yOffsets as number[]);
        if (values.rotationsDeg != null) setRotationsDeg(values.rotationsDeg as number[]);
        if (values.channelVisible != null) {
            setChannelVisible(values.channelVisible as boolean[]);
        }
    }, [setCompositeMode]);

    const { resetParameters } = useToolParamPersistence({
        toolId: "channel-split",
        defaultPreset: DEFAULT_CHANNEL_SPLIT_PRESET,
        getPresetValues,
        applyPreset,
    });

    useSvgToolHandoffReceiver(
        "channel-split",
        {
            onPrimary: (svg) => {
                setError(null);
                setSvg(svg);
            },
        },
        { setError },
    );

    const value: ChannelSplitState = {
        svg,
        error,
        svgInputRef,
        handleSvgLoad,
        compositeMode,
        setCompositeMode,
        distortionMethod,
        setDistortionMethod,
        pathNoiseStyle,
        setPathNoiseStyle,
        roughen,
        setRoughen,
        setRoughenAt,
        xOffsets,
        yOffsets,
        setXOffsetAt,
        setYOffsetAt,
        rotationsDeg,
        setRotationDegAt,
        resetOffsets,
        channelVisible,
        setChannelVisibleAt,
        resetChannelVisibility,
        viewBoxAttr,
        innerMarkup,
        vb,
        exportSvgString,
        canExport,
        handleDownload,
        applyPreset,
        getPresetValues,
        resetParameters,
    };

    return (
        <ChannelSplitContext.Provider value={value}>{children}</ChannelSplitContext.Provider>
    );
}
